/**
 * League "Ops Insights" sub-tab renderer.
 *
 * Pure HTML builders for the per-league operational dashboard (GET
 * /api/organizer/leagues/:id/analytics). Maps 1:1 to the Phase-1 metrics:
 *   - Predictive completion timeline (forecast).
 *   - Bottlenecks / pending prompts (sides delaying the active round).
 *   - Match density by round (completion velocity per round).
 *   - Competitiveness curve (blowouts vs. competitive vs. routine).
 *
 * Styling follows the two-skin model: night markup is base + `dark:`, and
 * `daylight:` classes are APPENDED for the light skin (never edited in).
 */

import { renderDormantPlayersCard } from "../../components/dormantPlayersCard";
import type {
  LeagueAnalytics,
  LeagueBottleneck,
  RoundDensity,
  CompetitivenessCurve,
  CompletionForecast,
} from "shared";

const esc = (value: string): string =>
  value.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      default: return "&#39;";
    }
  });

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

function emptyState(icon: string, title: string, body: string): string {
  return `
    <div class="text-center py-8">
      <span class="material-symbols-outlined text-4xl text-slate-300 dark:text-slate-700 daylight:text-day-ink-muted flex justify-center mb-2">${icon}</span>
      <h4 class="font-bold text-slate-900 dark:text-white daylight:text-day-ink">${esc(title)}</h4>
      <p class="text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft max-w-sm mx-auto mt-1">${esc(body)}</p>
    </div>
  `;
}

function sectionHeading(icon: string, iconClass: string, title: string): string {
  return `
    <div class="flex items-center gap-2 mb-4">
      <span class="material-symbols-outlined ${iconClass}">${icon}</span>
      <h3 class="text-xl font-bold text-slate-900 dark:text-white daylight:text-day-ink">${esc(title)}</h3>
    </div>
  `;
}

/** Predictive timeline: estimated completion date + round progress. */
function renderForecast(f: CompletionForecast): string {
  const date =
    f.estimatedCompletionDate !== null
      ? `<p class="text-3xl font-black text-slate-900 dark:text-white daylight:text-day-ink mt-1">${esc(formatDate(f.estimatedCompletionDate))}</p>`
      : `<p class="text-lg font-bold text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted mt-1">Not enough data yet</p>`;

  const cadence =
    f.avgDaysPerRound !== null
      ? `${f.avgDaysPerRound} days / round`
      : "No completed rounds to project from";

  return `
    <section class="card">
      ${sectionHeading("event", "text-primary daylight:text-day-ink", "Estimated Completion")}
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div class="sm:col-span-1">
          <p class="text-sm font-medium text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">Projected finish</p>
          ${date}
          <p class="text-xs text-slate-400 dark:text-slate-500 daylight:text-day-ink-muted mt-1">${esc(cadence)}</p>
        </div>
        <div class="sm:col-span-2 grid grid-cols-3 gap-3 self-center">
          ${miniStat(`${f.completedRounds}`, "Completed")}
          ${miniStat(`${f.remainingRounds}`, "Remaining")}
          ${miniStat(`${f.totalRounds}`, "Total rounds")}
        </div>
      </div>
    </section>
  `;
}

function miniStat(value: string, label: string): string {
  return `
    <div class="bg-slate-50 dark:bg-slate-800/50 daylight:bg-day-sunken rounded-lg px-3 py-2 text-center">
      <p class="text-2xl font-black text-slate-900 dark:text-white daylight:text-day-ink">${esc(value)}</p>
      <p class="text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${esc(label)}</p>
    </div>
  `;
}

/** Action-oriented list of the sides delaying the active round. */
function renderBottlenecks(bottlenecks: LeagueBottleneck[]): string {
  const inner =
    bottlenecks.length === 0
      ? emptyState("check_circle", "No bottlenecks", "No pending matches are holding up the active rounds.")
      : `
        <ul class="space-y-2">
          ${bottlenecks
            .map(
              (b) => `
            <li class="flex items-center justify-between gap-3 bg-slate-50 dark:bg-slate-800/50 daylight:bg-day-sunken rounded-lg px-4 py-3">
              <span class="font-bold text-slate-900 dark:text-white daylight:text-day-ink truncate">${esc(b.name || "TBD")}</span>
              <span class="shrink-0 badge badge-warning">${b.pendingMatches} pending</span>
            </li>`,
            )
            .join("")}
        </ul>
      `;

  return `
    <section class="card">
      ${sectionHeading("hourglass_top", "text-warning daylight:text-day-warning", "Bottlenecks / Pending Prompts")}
      ${inner}
    </section>
  `;
}

/** Per-round completion bars (the "density by group/tier" view). */
function renderDensity(rounds: RoundDensity[]): string {
  const inner =
    rounds.length === 0
      ? emptyState("schedule", "No rounds yet", "Round-by-round completion appears once rounds are scheduled.")
      : `
        <div class="space-y-3">
          ${rounds
            .map((r) => {
              const pct = Math.max(0, Math.min(100, r.completionPct));
              return `
              <div>
                <div class="flex items-center justify-between text-xs mb-1">
                  <span class="font-medium text-slate-700 dark:text-slate-300 daylight:text-day-ink-soft">Round ${r.roundNumber}</span>
                  <span class="font-mono text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${r.reportedMatches}/${r.totalMatches} · ${pct}%</span>
                </div>
                <div class="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-700 daylight:bg-day-sunken overflow-hidden" role="progressbar" aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100">
                  <div class="h-full rounded-full bg-primary daylight:shadow-day-ball transition-all" style="width: ${pct}%"></div>
                </div>
              </div>`;
            })
            .join("")}
        </div>
      `;

  return `
    <section class="card">
      ${sectionHeading("bar_chart", "text-primary daylight:text-day-ink", "Match Density by Round")}
      ${inner}
    </section>
  `;
}

/** Stacked competitiveness curve: blowouts vs. competitive vs. routine. */
function renderCompetitiveness(c: CompetitivenessCurve): string {
  if (c.totalReported === 0) {
    return `
      <section class="card">
        ${sectionHeading("balance", "text-success daylight:text-day-success", "Competitiveness Curve")}
        ${emptyState("scoreboard", "No results yet", "The blowout-vs-competitive breakdown appears once matches are reported.")}
      </section>
    `;
  }

  const segment = (pct: number, cls: string) =>
    pct > 0 ? `<div class="${cls} h-full" style="width: ${pct}%"></div>` : "";

  return `
    <section class="card">
      ${sectionHeading("balance", "text-success daylight:text-day-success", "Competitiveness Curve")}
      <div class="flex h-3 w-full rounded-full overflow-hidden bg-slate-200 dark:bg-slate-700 daylight:bg-day-sunken mb-4">
        ${segment(c.competitivePct, "bg-success daylight:bg-day-success")}
        ${segment(c.routinePct, "bg-warning daylight:bg-day-warning")}
        ${segment(c.blowoutPct, "bg-error daylight:bg-day-danger")}
      </div>
      <div class="grid grid-cols-3 gap-3">
        ${legendCard(c.competitivePct, c.competitive, "Competitive", "bg-success daylight:bg-day-success")}
        ${legendCard(c.routinePct, c.routine, "Routine", "bg-warning daylight:bg-day-warning")}
        ${legendCard(c.blowoutPct, c.blowouts, "Blowouts", "bg-error daylight:bg-day-danger")}
      </div>
    </section>
  `;
}

function legendCard(pct: number, count: number, label: string, dotClass: string): string {
  return `
    <div class="bg-slate-50 dark:bg-slate-800/50 daylight:bg-day-sunken rounded-lg px-3 py-2">
      <div class="flex items-center gap-2 mb-1">
        <span class="size-2.5 rounded-full ${dotClass}"></span>
        <span class="text-xs font-medium text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${esc(label)}</span>
      </div>
      <p class="text-2xl font-black text-slate-900 dark:text-white daylight:text-day-ink">${pct}%</p>
      <p class="text-xs text-slate-400 dark:text-slate-500 daylight:text-day-ink-muted">${count} matches</p>
    </div>
  `;
}

/**
 * Full Ops Insights dashboard for one league. Desktop layout:
 *   - Forecast (full width)
 *   - Competitiveness curve (full width) — the stacked bar reads better at 100%
 *   - Bottlenecks (left 50%) + Match Density (right 50%) side by side
 */
export function renderLeagueInsights(a: LeagueAnalytics): string {
  return `
    <div class="space-y-6">
      ${renderForecast(a.forecast)}
      ${renderCompetitiveness(a.competitivenessCurve)}
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        ${renderBottlenecks(a.bottlenecks)}
        ${renderDensity(a.densityByRound)}
      </div>
      ${renderDormantPlayersCard(a.dormantPlayers, { showLeagueColumn: false })}
    </div>
  `;
}

/** Loading placeholder shown while the analytics request is in flight. */
export function renderLeagueInsightsLoading(): string {
  return `
    <div class="flex items-center justify-center gap-3 py-16 text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">
      <span class="w-5 h-5 rounded-full border-2 border-primary/25 border-t-primary daylight:border-day-ink/15 daylight:border-t-day-ink animate-spin" aria-hidden="true"></span> Loading insights…
    </div>
  `;
}

/** Error placeholder shown when the analytics request fails. */
export function renderLeagueInsightsError(): string {
  return `
    <div class="text-center py-16">
      <span class="material-symbols-outlined text-4xl text-error mb-2 block">error_outline</span>
      <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">We couldn't load this league's insights. Please try again.</p>
    </div>
  `;
}
